import { useInkind } from '@gik/api/inkinds/inkind';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import { useDevStore } from '@gik/core/store/DevStore';
import { useInkindCan } from '@gik/core/store/permissions';
import { useUserStore } from '@gik/core/store/UserStore';
import bemBlock from '@gik/core/utils/bemBlock';
import getRandomInt from '@gik/core/utils/getRandomInt';
import repeatElement from '@gik/core/utils/repeatElement';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { updatePageInformation } from '@gik/inkind-page/api/InkindPageAPI';
import { InkindUserUpdates } from '@gik/inkind-page/components/InkindUserUpdates/InkindUserUpdates';
import { PageInformationInputNames } from '@gik/inkind-page/enums/PageInformationInputNames';
import { inkindPageInfoSchema } from '@gik/inkind-page/formSchemas';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { isInkindPageOrganizer, isInkindPageOwner } from '@gik/inkind-page/utils/inkindPagePermissions';
import { Button } from '@gik/ui/Button';
import type { IButtonProps } from '@gik/ui/Button/ButtonProps';
import { Form, FormField } from '@gik/ui/Form';
import { Heading } from '@gik/ui/Heading';
import { LoadingSpinner } from '@gik/ui/LoadingSpinner';
import { Separator } from '@gik/ui/Separator';
import { ParagraphBone } from '@gik/ui/SkeletonLoader';
import { SvgIcon } from '@gik/ui/SvgIcon/SvgIcon';
import { Text } from '@gik/ui/Text/Text';
import { UI } from '@gik/ui/UIManager';
import EditIcon from '@heroicons/react/outline/PencilIcon';
import he from 'he';
import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { inkindPageTourSelectors } from '../InkindPageTour/InkindPageTour';

interface IEditInkindPageInformationForm {
  [PageInformationInputNames.TITLE]: string;
  [PageInformationInputNames.STORY]: string;
  [PageInformationInputNames.NOTES]: string;
}

export interface IInkindPageInformationProps {
  inkindRouteId: string;
}

const blockName = 'inkind-page-information';

function InkindPageInformationComp({ inkindRouteId }: IInkindPageInformationProps) {
  const bem = bemBlock(blockName);
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const userId = useUserStore(state => state.id);
  const { data: inkindPage, mutate: mutateInkindPage } = useInkind(inkindRouteId);
  const isPageOwner = isInkindPageOwner(inkindPage?.owner?.userId, userId);
  const _canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId);
  const canEdit = isMounted() && _canEdit;
  const schema = inkindPageInfoSchema();
  const buttonsRef = React.useRef<HTMLDivElement>();

  const [loading, setLoading] = React.useState<boolean>(false);

  async function handleUpdateInkindPageInfo(values, isValid: boolean) {
    if (!isValid) return;
    setLoading(true);

    await updatePageInformation(inkindPage.routeId, {
      title: values[PageInformationInputNames.TITLE],
      description: values[PageInformationInputNames.STORY],
      specialNotes: values[PageInformationInputNames.NOTES],
    });

    setLoading(false);
    mutateInkindPage();

    UI.closeDialog();
  }

  async function openEditInkindPageInformationModal() {
    UI.dialog(
      <div className={bem('edit-inkind-page-information')}>
        {loading && <LoadingSpinner />}
        {!loading && (
          <Form
            initialValues={{
              [PageInformationInputNames.TITLE]: inkindPage.title ? he.decode(inkindPage.title) : null,
              [PageInformationInputNames.STORY]: inkindPage.description ? he.decode(inkindPage.description) : null,
              [PageInformationInputNames.NOTES]: inkindPage.notes ? he.decode(inkindPage.notes) : null,
            }}
            schema={schema}
            vertical
            id="page-information"
            restoreAfterUpdate
            render={({ isValid, values }) => {
              const buttons = (
                <>
                  <Button variant={'default'} onClick={() => UI.closeDialog()}>
                    Cancel
                  </Button>
                  <Button onClick={() => handleUpdateInkindPageInfo(values, isValid)} disabled={!isValid}>
                    Save
                  </Button>
                </>
              );

              return (
                <>
                  {schema.map((field, key) => (
                    <FormField key={key} className={bem('form-field')} name={field.name} center />
                  ))}

                  {buttonsRef?.current ? ReactDOM.createPortal(buttons, buttonsRef.current) : buttons}
                </>
              );
            }}
          />
        )}
      </div>,
      {
        title: t(translationKeys.EditInkindPageInformationModalTitle).toString(),
        buttons: <div ref={buttonsRef} />,
      }
    );
  }

  const forceSkeleton = useDevStore(state => state.forceSkeletons);
  const mounted = useIsMounted();

  const renderStory =
    inkindPage?.description || (mounted() && isPageOwner) || (mounted() && isInkindPageOrganizer(inkindPage, userId));
  const renderNotes =
    inkindPage?.notes || (mounted() && isPageOwner) || (mounted() && isInkindPageOrganizer(inkindPage, userId));

  return (
    <div className={bem()}>
      {isMounted() && <InkindUserUpdates inkindRouteId={inkindRouteId} />}

      {renderStory && (
        <div className={bem('story-wrapper')}>
          <div className={bem('title-wrapper')}>
            {mounted() && canEdit && inkindPage?.description && (
              <EditProfileInformationIconButton
                id={inkindPageTourSelectors.addStory}
                openEditInkindPageInformationModal={openEditInkindPageInformationModal}
              />
            )}
            <Heading level={2}>{t(translationKeys.InkindPageInformationStoryLabel).toString()}</Heading>
          </div>
          {forceSkeleton ? (
            <div className={bem('intro')}>
              <PageStorySkeleton />
            </div>
          ) : inkindPage?.description ? (
            <div className={bem('intro')}>
              <p>
                <Text parseUrls>{inkindPage?.description}</Text>
              </p>
            </div>
          ) : !canEdit ? (
            inkindPage ? (
              <div className={bem('intro', [{ empty: true }])}>
                <p>{t(translationKeys.InkindPageInformationIntroEmptyCopy).toString()}</p>
              </div>
            ) : (
              <div className={bem('intro')}>
                <PageStorySkeleton />
              </div>
            )
          ) : (
            <div className={bem('intro', [{ empty: true }, { organizer: true }])}>
              <EditProfileInformationTextButton
                text={t(translationKeys.EditProfileInformationTextButtonPageDescriptionLabel)}
                openEditInkindPageInformationModal={openEditInkindPageInformationModal}
              />
            </div>
          )}
        </div>
      )}

      {renderNotes && (
        <>
          <Separator thickness={'double'} type={'dashed'} />
          <div className={bem('special-notes-wrapper')}>
            <div className={bem('title-wrapper')}>
              {canEdit && inkindPage?.notes && (
                <EditProfileInformationIconButton
                  isAddSpecialNotes={true}
                  openEditInkindPageInformationModal={openEditInkindPageInformationModal}
                />
              )}
              <Heading level={2} variant={'danger'}>
                {t(translationKeys.InkindPageInformationSpecialNotesLabel).toString()}
              </Heading>
            </div>
            {forceSkeleton ? (
              <div className={bem('special-notes-content')}>
                <PageStorySkeleton />
              </div>
            ) : inkindPage?.notes ? (
              <div className={bem('special-notes-content')}>
                <p>
                  <Text parseUrls>{inkindPage?.notes}</Text>
                </p>
              </div>
            ) : !canEdit ? (
              inkindPage ? (
                <div className={bem('special-notes-content', [{ empty: true }])}>
                  <p>{t(translationKeys.InkindPageInformationSpecialNotesEmptyCopy).toString()}</p>
                </div>
              ) : (
                <div className={bem('special-notes-content')}>
                  <PageStorySkeleton />
                </div>
              )
            ) : (
              <div className={bem('special-notes-content', [{ empty: true }, { organizer: true }])}>
                <EditProfileInformationTextButton
                  text={t(translationKeys.EditProfileInformationTextButtonSpecialNotesLabel)}
                  isAddSpecialNotes={true}
                  openEditInkindPageInformationModal={openEditInkindPageInformationModal}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export const InkindPageInformation = withComponentErrorBoundary(InkindPageInformationComp);

interface IEditProfileInformationIconButtonProps extends IButtonProps {
  isAddSpecialNotes?: boolean;
  openEditInkindPageInformationModal: (isAddSpecialNotes: boolean) => void;
}

function EditProfileInformationIconButton({
  isAddSpecialNotes = false,
  openEditInkindPageInformationModal,
  ...buttonProps
}: IEditProfileInformationIconButtonProps) {
  const bem = bemBlock('edit-profile-information-icon-button');
  const { t } = useTranslation();

  return (
    <Button
      {...buttonProps}
      className={bem()}
      variant={'secondary'}
      onClick={() => openEditInkindPageInformationModal(isAddSpecialNotes)}
      size={'sm'}
      tooltip={t(translationKeys.EditProfileInformationIconButtonTooltip).toString()}
      circle
    >
      <SvgIcon Icon={EditIcon} />
    </Button>
  );
}

interface IEditProfileInformationTextButtonProps {
  text: string;
  isAddSpecialNotes?: boolean;
  openEditInkindPageInformationModal: (isAddSpecialNotes: boolean) => void;
}

function EditProfileInformationTextButton({
  text,
  isAddSpecialNotes = false,
  openEditInkindPageInformationModal,
}: IEditProfileInformationTextButtonProps) {
  const bem = bemBlock('edit-profile-information-text-button');

  return (
    <Button
      className={bem()}
      variant={'secondary'}
      onClick={() => openEditInkindPageInformationModal(isAddSpecialNotes)}
      prepend={<SvgIcon Icon={EditIcon} />}
    >
      {text}
    </Button>
  );
}

function PageStorySkeleton() {
  function getParagraph() {
    return <ParagraphBone words={getRandomInt(7, 20)} />;
  }

  return <>{repeatElement(getRandomInt(4, 9), getParagraph())}</>;
}
